<script>
  import { onMount, tick } from 'svelte';
  import { api } from '../lib/api.js';

  // The Annotate tab: work through every ecard product and record which card
  // titles (Mum, Age 40 Male, Sympathy Loss Of Dad, …) it still NEEDS, so the
  // design team knows what variants to create. Vocabulary comes from the server
  // (Archway master list ∪ live Shopify `title-` tags); annotations live in
  // Firestore (prod), one doc per product.

  let products = [];
  let titles = [];
  let christmasTitles = []; // Archway Xmas Counter Cards titles
  let loading = true;
  let error = null;
  let enabled = true;

  let q = '';
  let collection = '';
  let statusFilter = 'todo'; // todo | done | all
  let view = 'products';     // products | queue — queue is the builder's tick-off list
  let groupMode = 'title';   // queue grouping: title | design

  onMount(load);

  async function load() {
    loading = true;
    error = null;
    try {
      const [prodData, titleData] = await Promise.all([
        api('/annotations/products'),
        api('/annotations/titles'),
      ]);
      products = prodData.products || [];
      enabled = prodData.enabled !== false;
      titles = titleData.titles || [];
      christmasTitles = titleData.christmasTitles || [];
    } catch (e) {
      error = e.message;
    } finally {
      loading = false;
    }
  }

  $: collections = [...new Set(products.flatMap((p) => p.collections))].sort();
  $: doneCount = products.filter((p) => p.done).length;

  // Outcome of a requested title on a product: 'built' | 'skipped' | 'todo'.
  const isBuilt = (p, t) => p.builtTitles.some((b) => b.title === t);
  const isSkipped = (p, t) => p.skippedTitles.some((b) => b.title === t);
  const stateOf = (p, t) => (isBuilt(p, t) ? 'built' : isSkipped(p, t) ? 'skipped' : 'todo');

  // Search + collection apply to both views; the to-do/done status filter only
  // makes sense on the Products grid.
  $: baseFiltered = products.filter((p) => {
    if (collection && !p.collections.includes(collection)) return false;
    const needle = q.trim().toLowerCase();
    if (needle) {
      const hay = [p.title, ...p.collections, ...p.missingTitles].join(' ').toLowerCase();
      if (!hay.includes(needle)) return false;
    }
    return true;
  });
  $: filtered = baseFiltered.filter((p) => {
    if (statusFilter === 'todo' && p.done) return false;
    if (statusFilter === 'done' && !p.done) return false;
    return true;
  });

  // ---- Build queue ----------------------------------------------------------
  $: queueProducts = baseFiltered.filter((p) => p.missingTitles.length);
  $: queueStats = queueProducts.reduce((s, p) => {
    s.total += p.missingTitles.length;
    s.built += p.builtTitles.length;
    s.skipped += p.skippedTitles.length;
    return s;
  }, { total: 0, built: 0, skipped: 0 });

  // By-title grouping: most outstanding work first; fully-resolved titles sink.
  $: byTitle = (() => {
    const m = new Map();
    for (const p of queueProducts) {
      for (const t of p.missingTitles) {
        if (!m.has(t)) m.set(t, { title: t, todo: [], done: [] });
        (stateOf(p, t) === 'todo' ? m.get(t).todo : m.get(t).done).push(p);
      }
    }
    return [...m.values()].sort((a, b) => b.todo.length - a.todo.length || a.title.localeCompare(b.title));
  })();

  let builtError = '';
  async function setState(p, title, state) {
    // Optimistic update; revert on failure.
    const prev = { built: p.builtTitles, skipped: p.skippedTitles };
    p.builtTitles = prev.built.filter((b) => b.title !== title);
    p.skippedTitles = prev.skipped.filter((b) => b.title !== title);
    if (state === 'built') p.builtTitles = [...p.builtTitles, { title, builtAt: new Date().toISOString() }];
    if (state === 'skipped') p.skippedTitles = [...p.skippedTitles, { title, skippedAt: new Date().toISOString() }];
    products = products;
    builtError = '';
    try {
      await api(`/annotations/products/${p.id}/title-state`, {
        method: 'POST', body: JSON.stringify({ title, state }),
      });
    } catch (e) {
      p.builtTitles = prev.built;
      p.skippedTitles = prev.skipped;
      products = products;
      builtError = `${p.title} — ${e.message}`;
    }
  }

  // ---- Edit modal -----------------------------------------------------------
  let editing = null;        // product being annotated (working copy of fields)
  let selected = new Set();  // missing titles chosen in the modal
  let notes = '';
  let titleQ = '';
  let saving = false;
  let saveError = '';
  let titleSearchEl;
  let titleList = 'all';     // 'christmas' | 'all' — which list the picker shows

  const isChristmas = (p) => /christmas|xmas/i.test([p.title, ...p.collections].join(' '));

  function openEdit(p) {
    editing = p;
    titleList = christmasTitles.length && isChristmas(p) ? 'christmas' : 'all';
    selected = new Set(p.missingTitles);
    notes = p.notes || '';
    titleQ = '';
    saveError = '';
    tick().then(() => titleSearchEl && titleSearchEl.focus());
  }
  function closeEdit() {
    editing = null;
    saving = false;
    saveError = '';
  }

  function toggleTitle(t) {
    if (selected.has(t)) selected.delete(t);
    else selected.add(t);
    selected = selected; // trigger reactivity
  }

  $: listTitles = titleList === 'christmas' ? christmasTitles : titles;
  $: titleMatches = !titleQ.trim()
    ? listTitles
    : listTitles.filter((t) => t.toLowerCase().includes(titleQ.trim().toLowerCase()));

  async function save({ markDone, advance } = {}) {
    if (!editing) return;
    saving = true;
    saveError = '';
    const payload = {
      missingTitles: [...selected],
      notes,
      done: markDone !== undefined ? markDone : editing.done,
    };
    try {
      await api(`/annotations/products/${editing.id}`, { method: 'PUT', body: JSON.stringify(payload) });
      // Update the local row in place — no full reload between saves.
      Object.assign(editing, {
        missingTitles: payload.missingTitles,
        notes: payload.notes,
        done: payload.done,
        updatedAt: new Date().toISOString(),
      });
      products = products;
      const cur = editing;
      if (advance) {
        // Next not-done product in the current filtered view (after this one).
        const view = filtered.length ? filtered : products;
        const idx = view.indexOf(cur);
        const next = [...view.slice(idx + 1), ...view.slice(0, idx)].find((p) => !p.done && p !== cur);
        if (next) { openEdit(next); saving = false; return; }
      }
      closeEdit();
    } catch (e) {
      saveError = e.message;
      saving = false;
    }
  }

  // ---- CSV export -----------------------------------------------------------
  function exportCsv() {
    const esc = (v) => `"${String(v == null ? '' : v).replace(/"/g, '""')}"`;
    const rows = [
      ['productId', 'product', 'collections', 'missingTitles', 'builtTitles', 'skippedTitles', 'notes', 'done', 'updatedBy', 'updatedAt'],
      ...products.map((p) => [
        p.id, p.title, p.collections.join(' | '), p.missingTitles.join(' | '),
        p.builtTitles.map((b) => b.title).join(' | '),
        p.skippedTitles.map((b) => b.title).join(' | '),
        p.notes, p.done ? 'yes' : 'no', p.updatedBy || '', p.updatedAt || '',
      ]),
    ];
    const blob = new Blob([rows.map((r) => r.map(esc).join(',')).join('\n')], { type: 'text/csv' });
    const a = document.createElement('a');
    a.href = URL.createObjectURL(blob);
    a.download = `ecard-title-annotations-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(a.href);
  }

  const thumb = (url) => (url ? url + (url.includes('?') ? '&' : '?') + 'width=400' : null);
</script>

<div class="head">
  <div class="left">
    <h2>Annotate Ecards</h2>
    <div class="tabs">
      <button class="tab" class:on={view === 'products'} on:click={() => (view = 'products')}>Products</button>
      <button class="tab" class:on={view === 'queue'} on:click={() => (view = 'queue')}>Build queue</button>
    </div>
    {#if !loading && !error}
      {#if view === 'products'}
        <span class="progress" class:complete={doneCount === products.length}>
          {doneCount}/{products.length} done
        </span>
      {:else}
        <span class="progress" class:complete={queueStats.built + queueStats.skipped === queueStats.total && queueStats.total > 0}>
          {queueStats.built}/{queueStats.total} built{#if queueStats.skipped} · {queueStats.skipped} skipped{/if}
        </span>
      {/if}
    {/if}
  </div>
  <div class="controls">
    <input class="search" placeholder="Search title or collection…" bind:value={q} />
    <select bind:value={collection}>
      <option value="">All collections</option>
      {#each collections as c}<option value={c}>{c}</option>{/each}
    </select>
    {#if view === 'products'}
      <select bind:value={statusFilter}>
        <option value="todo">To do</option>
        <option value="done">Done</option>
        <option value="all">All</option>
      </select>
    {:else}
      <select bind:value={groupMode}>
        <option value="title">Group by title</option>
        <option value="design">Group by design</option>
      </select>
    {/if}
    <button on:click={exportCsv} disabled={loading || !products.length}>Export CSV</button>
    <button on:click={load} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
  </div>
</div>

{#if builtError}<p class="error">⚠️ {builtError}</p>{/if}

{#if loading}
  <p class="muted">Loading ecard products…</p>
{:else if error}
  <p class="error">⚠️ {error}</p>
{:else if !enabled}
  <p class="muted">Annotation storage isn't configured on this deployment (Firestore credentials missing).</p>
{:else if view === 'queue'}
  {#if queueProducts.length === 0}
    <p class="muted">Nothing in the build queue — no annotated products request any titles (matching the current filters).</p>
  {:else if groupMode === 'title'}
    <div class="queue">
      {#each byTitle as g (g.title)}
        <section class="qgroup" class:cleared={!g.todo.length}>
          <h3>
            {g.title}
            <span class="qcount">{#if g.todo.length}{g.todo.length} to build{:else}all resolved ✓{/if}</span>
          </h3>
          <div class="qcards">
            {#each [...g.todo, ...g.done] as p (p.id)}
              {@const st = stateOf(p, g.title)}
              <div class="qcard" class:built={st === 'built'} class:skipped={st === 'skipped'}>
                <div class="qimg">
                  {#if p.imageUrl}
                    <img src={thumb(p.imageUrl)} alt={p.title} loading="lazy" />
                  {:else}
                    <div class="noimg">No image</div>
                  {/if}
                  {#if p.url}<a class="qext" href={p.url} target="_blank" rel="noopener" title="Open on store">↗</a>{/if}
                  {#if st === 'built'}<span class="qstate ok">Built ✓</span>{/if}
                  {#if st === 'skipped'}<span class="qstate skip">Skipped</span>{/if}
                </div>
                <div class="qname" title={p.title}>{p.title}</div>
                <div class="qactions">
                  {#if st === 'todo'}
                    <button class="qa build" on:click={() => setState(p, g.title, 'built')} title="Mark '{g.title}' built for this design">✓ Built</button>
                    <button class="qa skip" on:click={() => setState(p, g.title, 'skipped')} title="Thin out — don't build '{g.title}' for this design">Skip</button>
                  {:else}
                    <button class="qa undo" on:click={() => setState(p, g.title, 'todo')} title="Put back in the queue">Undo</button>
                  {/if}
                </div>
              </div>
            {/each}
          </div>
        </section>
      {/each}
    </div>
  {:else}
    <div class="queue">
      {#each queueProducts as p (p.id)}
        {@const remaining = p.missingTitles.filter((t) => stateOf(p, t) === 'todo').length}
        <section class="qgroup" class:cleared={remaining === 0}>
          <h3 class="qdesign">
            {#if p.imageUrl}<img src={thumb(p.imageUrl)} alt="" loading="lazy" />{/if}
            <span class="qdesign-name">
              {p.title}
              {#if p.url}<a class="ext" href={p.url} target="_blank" rel="noopener">View on store ↗</a>{/if}
            </span>
            <span class="qcount">
              {#if remaining === 0}all resolved ✓{:else}{remaining} to build{/if}
            </span>
          </h3>
          <div class="qitems">
            {#each p.missingTitles as t (t)}
              {@const st = stateOf(p, t)}
              <span class="qitem" class:built={st === 'built'} class:skipped={st === 'skipped'}>
                <span class="qlabel">{t}</span>
                {#if st === 'todo'}
                  <button class="qa build" on:click={() => setState(p, t, 'built')} title="Mark built">✓</button>
                  <button class="qa skip" on:click={() => setState(p, t, 'skipped')} title="Thin out — don't build">✕</button>
                {:else}
                  <button class="qa undo" on:click={() => setState(p, t, 'todo')} title="Put back in the queue">undo</button>
                {/if}
              </span>
            {/each}
          </div>
        </section>
      {/each}
    </div>
  {/if}
{:else if filtered.length === 0}
  <p class="muted">
    {#if statusFilter === 'todo' && doneCount === products.length && products.length}
      🎉 All {products.length} ecards annotated.
    {:else}
      No products match the current filters.
    {/if}
  </p>
{:else}
  <div class="grid">
    {#each filtered as p (p.id)}
      <div class="card" class:done={p.done} role="button" tabindex="0"
           on:click={() => openEdit(p)}
           on:keydown={(ev) => (ev.key === 'Enter' || ev.key === ' ') && (ev.preventDefault(), openEdit(p))}>
        <div class="img">
          {#if p.imageUrl}
            <img src={thumb(p.imageUrl)} alt={p.title} loading="lazy" />
          {:else}
            <div class="noimg">No image</div>
          {/if}
          {#if p.url}
            <a class="qext" href={p.url} target="_blank" rel="noopener" title="View live ecard on store"
               on:click|stopPropagation>↗</a>
          {/if}
          {#if p.done}<span class="tick">✓</span>{/if}
        </div>
        <div class="body">
          <div class="title">{p.title}</div>
          {#if p.missingTitles.length}
            <div class="chips">
              {#each p.missingTitles.slice(0, 4) as t}
                <span class="chip" class:chip-built={isBuilt(p, t)} class:chip-skipped={isSkipped(p, t)}>{t}</span>
              {/each}
              {#if p.missingTitles.length > 4}<span class="chip more">+{p.missingTitles.length - 4}</span>{/if}
            </div>
          {:else if p.done}
            <div class="sub">No titles needed</div>
          {:else}
            <div class="sub">Not annotated yet</div>
          {/if}
          {#if p.notes}<div class="note-flag" title={p.notes}>📝 note</div>{/if}
        </div>
      </div>
    {/each}
  </div>
{/if}

<svelte:window on:keydown={(ev) => editing && ev.key === 'Escape' && closeEdit()} />

{#if editing}
  <div class="modal-backdrop" role="presentation" on:click|self={closeEdit}>
    <div class="modal" role="dialog" aria-modal="true">
      <div class="modal-head">
        <div class="modal-product">
          {#if editing.imageUrl}<img src={thumb(editing.imageUrl)} alt="" />{/if}
          <div>
            <h3>{editing.title}</h3>
            {#if editing.url}<a class="ext" href={editing.url} target="_blank" rel="noopener">View on store ↗</a>{/if}
          </div>
        </div>
        <button class="x" on:click={closeEdit} aria-label="Close">✕</button>
      </div>

      <p class="hint">Tick every title this design <strong>still needs</strong> to be made in. Leave empty and mark done if nothing is needed.</p>

      {#if selected.size}
        <div class="chips selected-chips">
          {#each [...selected] as t}
            <button class="chip removable" class:chip-built={isBuilt(editing, t)} class:chip-skipped={isSkipped(editing, t)}
                    on:click={() => toggleTitle(t)} title={isBuilt(editing, t) ? 'Already built — remove' : isSkipped(editing, t) ? 'Skipped by the builder — remove' : 'Remove'}>{t} ✕</button>
          {/each}
        </div>
      {/if}

      {#if christmasTitles.length}
        <div class="list-switch">
          <button class:active={titleList === 'christmas'} on:click={() => (titleList = 'christmas')}>🎄 Christmas ({christmasTitles.length})</button>
          <button class:active={titleList === 'all'} on:click={() => (titleList = 'all')}>All titles ({titles.length})</button>
        </div>
      {/if}
      <input class="title-search" placeholder="Search {listTitles.length} titles… (e.g. mum, age 40, sympathy)"
             bind:value={titleQ} bind:this={titleSearchEl} />
      <div class="title-list">
        {#each titleMatches as t (t)}
          <label class="opt">
            <input type="checkbox" checked={selected.has(t)} on:change={() => toggleTitle(t)} />
            <span>{t}</span>
          </label>
        {:else}
          <p class="muted small">No titles match "{titleQ}".</p>
        {/each}
      </div>

      <label class="field">
        <span>Notes (optional)</span>
        <textarea rows="2" maxlength="2000" bind:value={notes} placeholder="Anything unusual about this design…"></textarea>
      </label>

      {#if saveError}<p class="error">⚠️ {saveError}</p>{/if}

      <div class="modal-actions">
        <button class="secondary" on:click={closeEdit} disabled={saving}>Cancel</button>
        {#if editing.done}
          <button class="secondary" on:click={() => save({ markDone: false })} disabled={saving}>Mark not done</button>
        {/if}
        <button class="secondary" on:click={() => save({})} disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
        <button class="primary" on:click={() => save({ markDone: true, advance: true })} disabled={saving}>
          {saving ? 'Saving…' : 'Save & mark done'}
        </button>
      </div>
    </div>
  </div>
{/if}

<style>
  .head { display: flex; justify-content: space-between; align-items: center; gap: 1rem; flex-wrap: wrap; }
  .left { display: flex; align-items: center; gap: .8rem; }
  h2 { font-size: 1.25rem; margin: 0; }
  .progress { font-size: .8rem; font-weight: 600; color: #854d0e; background: #fef9c3; border-radius: 999px; padding: .15rem .6rem; }
  .progress.complete { color: #166534; background: #dcfce7; }
  .controls { display: flex; gap: .5rem; align-items: center; flex-wrap: wrap; }
  .search { padding: .35rem .55rem; border: 1px solid #ccc; border-radius: 6px; font-size: .85rem; min-width: 220px; }
  select { padding: .35rem .45rem; border: 1px solid #ccc; border-radius: 6px; font-size: .85rem; background: #fff; max-width: 15rem; }
  button { cursor: pointer; border: 1px solid #ccc; background: #fff; padding: .4rem .8rem; border-radius: 6px; }
  .muted { color: #666; }
  .muted.small { font-size: .8rem; padding: .4rem; }
  .error { color: #b00020; }

  .grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(170px, 1fr)); gap: .9rem; margin-top: 1rem; }
  .card { display: flex; flex-direction: column; text-align: left; padding: 0; border: 1px solid #e5e5e5; border-radius: 10px; background: #fff; overflow: hidden; box-shadow: 0 1px 2px rgba(0,0,0,.05); transition: box-shadow .12s, transform .12s; cursor: pointer; }
  /* Grid cards: store link sits top-LEFT so it never collides with the done ✓ */
  .card .qext { right: auto; left: .3rem; }
  .card:hover { box-shadow: 0 4px 12px rgba(0,0,0,.12); transform: translateY(-1px); }
  .card.done { border-color: #bbf7d0; }
  .img { position: relative; aspect-ratio: 1 / 1; background: #f4f4f4; }
  .img img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .noimg { display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: .8rem; }
  .tick { position: absolute; top: .4rem; right: .4rem; width: 1.5rem; height: 1.5rem; display: flex; align-items: center; justify-content: center; background: #16a34a; color: #fff; border-radius: 50%; font-size: .85rem; font-weight: 700; }
  .body { padding: .55rem .65rem .65rem; display: flex; flex-direction: column; gap: .35rem; }
  .title { font-size: .84rem; font-weight: 600; line-height: 1.25; }
  .sub { color: #999; font-size: .75rem; }
  .chips { display: flex; flex-wrap: wrap; gap: .25rem; }
  .chip { background: #f3e8ff; color: #7e22ce; border-radius: 999px; padding: .08rem .5rem; font-size: .7rem; font-weight: 600; }
  .chip.more { background: #eee; color: #555; }
  .chip.chip-built { background: #dcfce7; color: #166534; text-decoration: line-through; }
  .chip.chip-skipped { background: #f1f5f9; color: #64748b; text-decoration: line-through; }
  .note-flag { font-size: .7rem; color: #92400e; }

  .tabs { display: flex; gap: .25rem; background: #eee; border-radius: 8px; padding: .15rem; }
  .tab { border: none; background: none; padding: .3rem .8rem; border-radius: 6px; font-size: .82rem; font-weight: 600; color: #666; }
  .tab.on { background: #fff; color: #111; box-shadow: 0 1px 2px rgba(0,0,0,.1); }

  .queue { display: flex; flex-direction: column; gap: .9rem; margin-top: 1rem; }
  .qgroup { background: #fff; border: 1px solid #e5e5e5; border-radius: 10px; padding: .7rem .9rem .8rem; box-shadow: 0 1px 2px rgba(0,0,0,.05); }
  .qgroup.cleared { opacity: .55; }
  .qgroup h3 { margin: 0 0 .5rem; font-size: .95rem; display: flex; align-items: center; gap: .5rem; flex-wrap: wrap; }
  .qdesign img { width: 4.5rem; height: 4.5rem; object-fit: cover; border-radius: 8px; border: 1px solid #eee; }
  .qdesign-name { display: flex; flex-direction: column; gap: .15rem; }
  .qcount { font-size: .72rem; font-weight: 600; color: #854d0e; background: #fef9c3; border-radius: 999px; padding: .1rem .5rem; margin-left: auto; }
  .qgroup.cleared .qcount { color: #166534; background: #dcfce7; }

  /* By-title: big identifiable cards with Built / Skip actions */
  .qcards { display: grid; grid-template-columns: repeat(auto-fill, minmax(150px, 1fr)); gap: .7rem; }
  .qcard { border: 1px solid #e5e5e5; border-radius: 10px; overflow: hidden; background: #fafafa; display: flex; flex-direction: column; }
  .qcard.built { border-color: #bbf7d0; background: #f0fdf4; }
  .qcard.skipped { border-color: #e2e8f0; background: #f8fafc; opacity: .75; }
  .qimg { position: relative; aspect-ratio: 1 / 1; background: #f0f0f0; }
  .qimg img { width: 100%; height: 100%; object-fit: cover; display: block; }
  .qimg .noimg { display: flex; align-items: center; justify-content: center; height: 100%; color: #999; font-size: .75rem; }
  .qext { position: absolute; top: .3rem; right: .3rem; background: rgba(255,255,255,.9); color: #7e22ce; border-radius: 6px; width: 1.4rem; height: 1.4rem; display: flex; align-items: center; justify-content: center; text-decoration: none; font-size: .85rem; }
  .qext:hover { background: #fff; }
  .qstate { position: absolute; left: .3rem; bottom: .3rem; font-size: .68rem; font-weight: 700; border-radius: 6px; padding: .12rem .45rem; }
  .qstate.ok { background: #16a34a; color: #fff; }
  .qstate.skip { background: #64748b; color: #fff; }
  .qname { font-size: .76rem; font-weight: 600; line-height: 1.2; padding: .4rem .5rem .1rem; overflow: hidden; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
  .qcard.skipped .qname, .qcard.built .qname { text-decoration: line-through; color: #64748b; }
  .qactions { display: flex; gap: .35rem; padding: .4rem .5rem .5rem; margin-top: auto; }
  .qa { flex: 1; border-radius: 6px; padding: .3rem .4rem; font-size: .74rem; font-weight: 600; cursor: pointer; }
  .qa.build { border: 1px solid #86efac; background: #dcfce7; color: #166534; }
  .qa.build:hover { background: #bbf7d0; }
  .qa.skip { border: 1px solid #cbd5e1; background: #f1f5f9; color: #475569; }
  .qa.skip:hover { background: #e2e8f0; }
  .qa.undo { border: 1px solid #ddd; background: #fff; color: #555; }
  .qa.undo:hover { background: #f5f5f5; }

  /* By-design: title pills with inline ✓ / ✕ actions */
  .qitems { display: flex; flex-wrap: wrap; gap: .4rem; }
  .qitem { display: inline-flex; align-items: center; gap: .3rem; border: 1px solid #ddd; background: #fafafa; border-radius: 999px; padding: .18rem .3rem .18rem .7rem; font-size: .8rem; }
  .qitem.built { background: #f0fdf4; border-color: #bbf7d0; color: #166534; }
  .qitem.skipped { background: #f8fafc; border-color: #e2e8f0; color: #64748b; }
  .qitem.built .qlabel, .qitem.skipped .qlabel { text-decoration: line-through; }
  .qitem .qa { flex: none; padding: .12rem .5rem; font-size: .7rem; }

  .modal-backdrop { position: fixed; inset: 0; background: rgba(0,0,0,.45); display: flex; align-items: flex-start; justify-content: center; padding: 4vh 1rem; z-index: 50; overflow-y: auto; }
  .modal { background: #fff; border-radius: 12px; width: 100%; max-width: 34rem; padding: 1.25rem 1.5rem 1.5rem; box-shadow: 0 12px 40px rgba(0,0,0,.25); }
  .modal-head { display: flex; justify-content: space-between; align-items: flex-start; }
  .modal-product { display: flex; gap: .8rem; align-items: center; }
  .modal-product img { width: 3.4rem; height: 3.4rem; object-fit: cover; border-radius: 8px; border: 1px solid #eee; }
  .modal-head h3 { margin: 0; font-size: 1.05rem; }
  .ext { font-size: .78rem; color: #9333ea; text-decoration: none; }
  .ext:hover { text-decoration: underline; }
  .x { border: none; background: none; font-size: 1rem; color: #888; cursor: pointer; padding: .2rem .4rem; }
  .x:hover { color: #333; }
  .hint { font-size: .82rem; color: #555; margin: .6rem 0 .4rem; }
  .selected-chips { margin-bottom: .5rem; }
  .chip.removable { cursor: pointer; border: none; font: inherit; font-size: .72rem; font-weight: 600; }
  .chip.removable:hover { background: #e9d5ff; }
  .list-switch { display: flex; gap: .4rem; margin: .4rem 0 .5rem; }
  .list-switch button { padding: .3rem .7rem; border: 1px solid #ccc; border-radius: 999px; background: #fff; color: #444; font-size: .8rem; cursor: pointer; }
  .list-switch button.active { border-color: #7e22ce; background: #f3e8ff; color: #6b21a8; font-weight: 600; }
  .title-search { width: 100%; box-sizing: border-box; padding: .45rem .55rem; border: 1px solid #ccc; border-radius: 6px 6px 0 0; font-size: .88rem; }
  .title-list { border: 1px solid #ccc; border-top: none; border-radius: 0 0 6px 6px; max-height: 15rem; overflow-y: auto; }
  .opt { display: flex; gap: .5rem; align-items: center; padding: .3rem .6rem; font-size: .84rem; cursor: pointer; }
  .opt:hover { background: #faf5ff; }
  .opt input { accent-color: #7e22ce; }
  .field { display: flex; flex-direction: column; gap: .25rem; margin-top: .7rem; font-size: .82rem; color: #444; }
  .field > span { font-weight: 600; }
  .field textarea { padding: .45rem .55rem; border: 1px solid #ccc; border-radius: 6px; font: inherit; font-size: .88rem; }
  .modal-actions { display: flex; justify-content: flex-end; gap: .6rem; margin-top: 1.2rem; flex-wrap: wrap; }
  .modal-actions button { padding: .5rem 1rem; border-radius: 6px; font-weight: 600; font-size: .85rem; }
  .modal-actions .secondary { border: 1px solid #ccc; background: #fff; color: #444; }
  .modal-actions .primary { border: 1px solid #7e22ce; background: #7e22ce; color: #fff; }
  .modal-actions button:disabled { opacity: .6; cursor: default; }
</style>
